import { tokenDeAcceso } from "./supabase";

export const BASE = import.meta.env["VITE_API_URL"] ?? "http://localhost:3000/api";

export class ErrorApi extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly cuerpo?: unknown,
  ) {
    super(message);
    this.name = "ErrorApi";
  }
}

/**
 * La API respondió 202: la operación quedó EN PROCESO.
 *
 * Pasa cuando el PAC no contestó a tiempo. Es el caso que la interfaz debe
 * tratar con más cuidado: hay que decirle al usuario que NO vuelva a
 * intentar y dejar que la reconciliación del backend resuelva.
 */
export class OperacionEnProceso extends Error {
  constructor(
    message: string,
    readonly detalle?: string,
  ) {
    super(message);
    this.name = "OperacionEnProceso";
  }
}

interface OpcionesPeticion {
  method?: string;
  body?: unknown;
  /** Se manda como header Idempotency-Key. Obligatorio al timbrar. */
  claveIdempotencia?: string;
  signal?: AbortSignal;
}

export async function api<T>(
  ruta: string,
  opciones: OpcionesPeticion = {},
): Promise<T> {
  const token = await tokenDeAcceso();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (opciones.claveIdempotencia) {
    headers["Idempotency-Key"] = opciones.claveIdempotencia;
  }

  const respuesta = await fetch(`${BASE}${ruta}`, {
    method: opciones.method ?? "GET",
    headers,
    body: opciones.body ? JSON.stringify(opciones.body) : undefined,
    signal: opciones.signal,
  });

  if (respuesta.status === 204) return undefined as T;

  const cuerpo = await respuesta.json().catch(() => null);

  if (respuesta.status === 202) {
    const c = cuerpo as { message?: string; detalle?: string } | null;
    throw new OperacionEnProceso(
      c?.message ?? "La operación quedó en proceso",
      c?.detalle,
    );
  }

  if (!respuesta.ok) {
    const c = cuerpo as { message?: string; errores?: unknown } | null;
    throw new ErrorApi(
      c?.message ?? `Error ${respuesta.status}`,
      respuesta.status,
      cuerpo,
    );
  }

  return cuerpo as T;
}

/**
 * Para descargas binarias (PDF, XML): `api()` siempre intenta leer JSON, y
 * un PDF no lo es. `fetch` en un `<a href>` no manda el header
 * `Authorization`, así que la descarga tiene que pasar por aquí y armar un
 * blob URL en el navegador en vez de enlazar la ruta directo.
 */
export async function apiBlob(ruta: string): Promise<Blob> {
  const token = await tokenDeAcceso();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const respuesta = await fetch(`${BASE}${ruta}`, { headers });
  if (!respuesta.ok) {
    const cuerpo = await respuesta.json().catch(() => null);
    const c = cuerpo as { message?: string } | null;
    throw new ErrorApi(c?.message ?? `Error ${respuesta.status}`, respuesta.status, cuerpo);
  }
  return respuesta.blob();
}
